import Icon from "components/Icons/Icon";
import useAppStoreLink from "components/common/useAppStoreLink";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import { useEffect, useState } from "react";
import { getHomeTranslations } from "../marketing/i18n/pages/home";

// Same brand "S" mark used by HeroShowcase.tsx's S_PATH / BlogBanner.tsx's S_MARK_PATH /
// FeatureToolboxSection.tsx's S_PATH, kept in its native 81x108 coordinate space so the
// SVG viewBox scales it automatically.
const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

const ClosingCTASection = () => {
	// mounted-gate avoids an osName-driven hydration mismatch when this section renders
	// through the SSR marketing pipeline (see HomeMarketing.tsx) — "/register" is a stable
	// literal that matches on both server and the pre-hydration client pass.
	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		setMounted(true);
	}, []);

	const { appstoreUrl, handleTrack } = useAppStoreLink("marketing_site");
	const appDownloadHref = mounted ? appstoreUrl : "/register";
	// 123yb691qz1: this CTA points at /register, so it opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. "Download the app" above never went through this — it always pointed at the
	// store — so it keeps its own `useAppStoreLink` tracking untouched.
	const gate = usePlanGate("homepage_closing_cta", "store");

	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);

	return (
		<>
		<section className="closing-cta">
			<div className="closing-cta__mark" aria-hidden="true">
				<svg width="100%" height="100%" viewBox="0 0 81 108" preserveAspectRatio="xMidYMid slice">
					<path d={S_PATH} fill="white" />
				</svg>
			</div>

			<div className="closing-cta__inner">
				<div className="closing-cta__content">
					<div className="closing-cta__rating">
						{[0, 1, 2, 3, 4].map((i) => (
							<Icon key={i} name="star--filled" size={14} fill="#ffc14a" />
						))}
						<span>{t(translations, "home.closingCta.rating", "4.7 App Store")}</span>
					</div>

					<h2>
						{t(translations, "home.closingCta.headingPrefix", "Your next trip starts ")}
						<span className="closing-cta__accent">{t(translations, "home.closingCta.headingAccent", "here.")}</span>
					</h2>

					<p>{t(translations, "home.closingCta.subtitle", "Free forever. Plan your route, collaborate with friends, and take it all offline.")}</p>

					<div className="closing-cta__ctas">
						<a
							href="/register"
							className="btn-rounded solid xl"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "home.closingCta.primaryCta", "Start planning, it's free →")}
						</a>
						<a href={appDownloadHref} className="btn-rounded outlined light xl" onClick={handleTrack} target="_blank" rel="noreferrer">
							<svg width="13" height="17" viewBox="0 0 81 108" fill="none">
								<path d={S_PATH} />
							</svg>
							{t(translations, "home.closingCta.secondaryCta", "Download the app")}
						</a>
					</div>
				</div>
			</div>
		</section>
		<PlanGateModal
			gate={gate}
			title={t(translations, "home.auth.startPlanning", "Create a free account to start planning")}
			loginTitle={t(translations, "home.auth.login", "Log in to plan with AI")}
		/>
		</>
	);
};

export default ClosingCTASection;
